import { Eye, MailCheck, MailWarning, MailX } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Small inline pill showing the read/delivery state of a sent invoice,
 * driven by Resend's webhook fan-out.
 *
 * Priority (worst → best signal wins):
 *   bounced > complained > viewed > delivered
 *
 * Renders nothing when the invoice has no lifecycle timestamps.
 */
export default function ReadReceiptBadge({ invoice }) {
  if (!invoice) return null;

  const {
    bounced_at,
    complained_at,
    last_viewed_at,
    view_count,
    delivered_at,
    bounce_reason,
  } = invoice;

  if (bounced_at) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-red-50 text-red-700 border-red-200"
        title={bounce_reason ? `Bounced: ${bounce_reason}` : 'Bounced'}
        data-testid={`invoice-status-bounced-${invoice.id}`}
      >
        <MailX className="h-3 w-3" />
        Bounced
      </span>
    );
  }

  if (complained_at) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-orange-50 text-orange-700 border-orange-200"
        title={`Marked as spam ${formatWhen(complained_at)}`}
        data-testid={`invoice-status-complained-${invoice.id}`}
      >
        <MailWarning className="h-3 w-3" />
        Complaint
      </span>
    );
  }

  if (last_viewed_at) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-emerald-50 text-emerald-700 border-emerald-200"
        title={
          `Client opened ${view_count || 1} time${(view_count || 1) === 1 ? '' : 's'}, ` +
          `most recent ${formatWhen(last_viewed_at)}`
        }
        data-testid={`invoice-status-viewed-${invoice.id}`}
      >
        <Eye className="h-3 w-3" />
        Viewed{view_count && view_count > 1 ? ` ×${view_count}` : ''}
      </span>
    );
  }

  if (delivered_at) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-sky-50 text-sky-700 border-sky-200"
        title={`Delivered ${formatWhen(delivered_at)}`}
        data-testid={`invoice-status-delivered-${invoice.id}`}
      >
        <MailCheck className="h-3 w-3" />
        Delivered
      </span>
    );
  }

  return null;
}

function formatWhen(iso) {
  try {
    return `${formatDistanceToNow(parseISO(iso))} ago`;
  } catch {
    return iso;
  }
}
